import HubService from '@/client/services/api/HubService';
import { HubChild } from '@/client/types/admin/hubs/HubChild';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

export const HUB_CHILDREN_QUERY_KEY = ['hub', 'children'];

/**
 * The caller's granted hub children — also the "am I a hub admin?" probe.
 * Hub-admin membership lives on the API-side Hub doc, not in the auth
 * payload, so the only way to know is to ask: 403/404 means the company
 * isn't a hub parent or this admin has no grant entry.
 *
 * `staleTime: Infinity` + `retry: false` are deliberate: a revoked grant
 * keeps the hub nav visible until a full reload, which is cosmetic only
 * (the API 403s every hub call). Do not "fix" the 403s by retrying.
 */
export const useHubChildren = () => {
  const { allowance } = useAccessHelper();

  return useQuery({
    queryKey: HUB_CHILDREN_QUERY_KEY,
    queryFn: HubService.getHubChildren,
    enabled: allowance.hubs,
    retry: false,
    staleTime: Infinity,
  });
};

export const useIsHubAdmin = () => {
  const query = useHubChildren();

  return {
    // Only a successful children read proves hub-admin membership;
    // errors and the not-yet-fetched state both gate the UI off.
    isHubAdmin: query.isSuccess,
    isLoading: query.isInitialLoading,
  };
};

/**
 * The granted child list, name-filtered client-side (the grant-bounded list
 * arrives whole — hubs are small). Shared by every surface that offers a
 * searchable child list (orgs table, distribute picker).
 */
export const useFilteredHubChildren = (searchTerm: string) => {
  const query = useHubChildren();

  const children = useMemo<HubChild[]>(() => {
    const all = query.data?.children ?? [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return all;

    return all.filter((child) => child.name.toLowerCase().includes(term));
  }, [query.data, searchTerm]);

  return {
    children,
    isLoading: query.isInitialLoading,
    isLoaded: query.isSuccess,
    // Inside the hub console a failure here is a real error (the layout's
    // probe already redirected non-hub-admins away) — distinct from an
    // empty grant, which is a successful load with zero children.
    isError: query.isError,
  };
};
